import React from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { Button, Decorator, Input, Tooltip } from '@myhealth-raccoon/components';
import { DateInput, validation } from '@myhealth-raccoon/forms';
import { HelpCircle } from "@untitled-ui/icons-react";
import { BackButton } from "src/components/BackButton";

import { useRememberFormValues } from 'src/hooks/useRememberFormValues';
import { useClinicSelector } from 'src/hooks/useAsyncSelector';
import { StickyBottomBar } from '../StickyBottomBar';
import { ContentContainer } from '../ContentContainer';

type Props = {
    onSubmit: (values: PersonalDetailsFormValues) => void;
    onBack?(): void;
    className?: string;
}

export type PersonalDetailsFormValues = {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    mobileNumber: string;

    rememberMe: boolean;
    termsOfServiceConsent: boolean;
    cancellationPolicyConsent: boolean;
}

export const PersonalDetailsForm = React.forwardRef<HTMLFormElement, Props>(({
    onSubmit,
    onBack,
    className
}, ref) => {

    const [defaultValues, rememberDefaultValues] = useRememberFormValues<PersonalDetailsFormValues>('personalDetailsForm', "local");

    const { data: clinic, ...clinicRequest } = useClinicSelector();

    const {
        handleSubmit: innerSubmit,
        register,
        control,
        formState: { isSubmitting, errors },
    } = useForm<PersonalDetailsFormValues>({ defaultValues: defaultValues ?? {} });

    register("dateOfBirth", {
        validate: validation.composeValidators(
            validation.required('Please enter a date of birth'),
            validation.dob('Please enter a valid date of birth'),
        )
    });

    return <form className={classNames("flex flex-col", className)} ref={ref} onSubmit={innerSubmit(async (values) => {
        rememberDefaultValues(values, "session");
        rememberDefaultValues(values.rememberMe ? values : undefined, "local");
        await onSubmit(values);
    })}>
        <ContentContainer className='flex-1 lg:flex-none flex flex-col gap-y-5'>
            <Decorator fieldset
                label="Legal name"
                hint="Enter name exactly as it is recorded with Medicare or an ID."
                requiredIndicator
            >
                <Input
                    id="firstName"
                    {...register("firstName", { validate: validation.required('Please enter a first name') })}
                    fieldError={errors.firstName}
                    placeholder='First name'
                    className='mb-2.5'
                    type="text"
                    autoCorrect="off"
                    autoCapitalize="words"
                    spellCheck="false"
                />
                <Input
                    id="lastName"
                    {...register("lastName", { validate: validation.required('Please enter a last name') })}
                    fieldError={errors.lastName}
                    placeholder='Last name'
                    type="text"
                    autoCorrect="off"
                    autoCapitalize="words"
                    spellCheck="false"
                />
            </Decorator>

            <DateInput
                label="Date of birth"
                control={control}
                name='dateOfBirth'
                id='dateOfBirth'
                fieldError={errors.dateOfBirth}
                requiredIndicator
            />

            <Input
                id="mobileNumber"
                {...register("mobileNumber", { validate: validation.composeValidators(validation.required('Please enter a mobile number'), validation.phoneNumber('Please enter a valid mobile number')) })}
                fieldError={errors.mobileNumber}
                type='tel'
                placeholder='0412 345 678'
                label="Mobile number"
                requiredIndicator
            />


            <Decorator fieldError={errors.rememberMe} >
                <div className='flex flex-row items-start justify-start gap-2 text-gray-700 font-semibold text-sm'>
                    <input className='checkbox mt-0.5' id="rememberMe" {...register("rememberMe")} type='checkbox' />
                    <label className='cursor-pointer' htmlFor='rememberMe'>Remember details</label>
                    <Tooltip
                        tooltipContent={<div>What is 'Remember details'?<br />
                            We’ll remember your details on this device. Unchecking it will remove them.</div>}
                        attachmentPoint='top'
                        className='inline-block align-middle'
                    >
                        <HelpCircle className='w-4 h-4 text-gray-400' />
                    </Tooltip>
                </div>
            </Decorator>

            <Decorator fieldError={errors.termsOfServiceConsent}>
                <div className='flex flex-row items-start justify-start gap-2 text-gray-700 font-semibold text-sm'>
                    <input className='checkbox mt-0.5' id="termsOfServiceConsent" {...register("termsOfServiceConsent", { validate: validation.required() })} type='checkbox' />
                    <label className='cursor-pointer' htmlFor='termsOfServiceConsent'>
                        I consent to Myhealth's{' '}

                        <a target="_blank" href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}/privacy-policy`} className='text-primary underline'>
                            Privacy Policy
                        </a>
                        {' '}and our{' '}
                        <a target="_blank" href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}/information-collection-notice`} className='text-primary underline'>
                            Information Collection Policy
                        </a>
                        . <span aria-label='Required' className='text-primary'>*</span>
                    </label>
                </div>
            </Decorator>

            <Decorator fieldError={errors.cancellationPolicyConsent}>
                <div className='flex flex-row items-start justify-start gap-2 text-gray-700 font-semibold text-sm'>
                    <input className='checkbox mt-0.5' id="cancellationPolicyConsent" {...register("cancellationPolicyConsent", { validate: validation.required() })} type='checkbox' />
                    <label className='cursor-pointer' htmlFor='cancellationPolicyConsent'>
                        I agree to the{' '}
                        <a target="_blank" href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}${clinic?.url}#pricing`} className='text-primary underline'>
                            Consultation Fees
                        </a>
                        {' '}&{' '}
                        <a target="_blank" href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}${clinic?.url}#cancellation-policy`} className='text-primary underline'>
                            Cancellation policy
                        </a>
                        {' '}of {clinic ? clinic?.name : clinicRequest.error ? clinicRequest.error.message : "..."}
                        . <span aria-label='Required' className='text-primary'>*</span>
                    </label>
                </div>
            </Decorator>

        </ContentContainer>

        <StickyBottomBar>
            <BackButton
                onClick={onBack}
                label="Back"
            />
            <Button className='flex-1 text-center' disabled={isSubmitting} type='submit'>Next</Button>
        </StickyBottomBar>
    </form>
})